import { IconName, library } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Tooltip } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"

/** Map legacy FA4 icon names to their FA6 equivalents. */
const fa4ToFa6: Record<string, string> = {
  "pencil-square-o": "pen-to-square",
  "pencil-square": "pen-to-square",
  "pencil": "pen",
  "remove": "xmark",
  "close": "xmark",
  "trash-o": "trash-can",
  "star-o": "star",
  "heart-o": "heart",
  "envelope-o": "envelope",
  "file-o": "file",
  "clock-o": "clock",
  "arrow-circle-o-right": "circle-arrow-right",
  "arrow-circle-o-left": "circle-arrow-left",
  "times-circle-o": "circle-xmark",
  "check-circle-o": "circle-check",
  "ban": "ban",
  "bar-chart": "chart-simple",
  "area-chart": "chart-area",
}

/**
 * Normalise an icon value from the DB to a FA6 icon name.
 * Handles FA4 legacy names, "fa-" prefixes, and style prefixes.
 */
export const toIconName = (raw: string | null | undefined): IconName => {
  if (!raw) return "tag"
  let cleaned = raw
    .replace(/^fa-solid\s+/, "")
    .replace(/^fa-regular\s+/, "")
    .replace(/^fa-/, "")
    .replace(/-o$/, "-o")
  cleaned = fa4ToFa6[cleaned] || cleaned.replace(/-o$/, "") || cleaned
  return (cleaned || "tag") as IconName
}

/** Renders a Font Awesome icon (SVG-based). */
export const FaIcon: FC<{ icon: string; color?: string; size?: number }> = ({
  icon,
  color,
  size = 16,
}) => (
  <FontAwesomeIcon
    icon={["fas", toIconName(icon)]}
    style={{ fontSize: size, color: color || "inherit" }}
  />
)

/** Get all solid icon names from the FA library (cached). */
let _iconCache: IconName[] | null = null
export const getAllSolidIconNames = (): IconName[] => {
  if (_iconCache) return _iconCache
  const defs = (library as any).definitions?.["fas"]
  if (!defs) return []
  _iconCache = Object.keys(defs).sort() as IconName[]
  return _iconCache
}

type TagData = {
  label?: string
  shortLabel?: string
  color?: string
  icon?: string
  displayOption?: string
}

/**
 * Renders a tag chip respecting `displayOption`, `color`, and `icon`.
 *
 * Display options:
 *  - ICON_SHORT (or default): icon + short label
 *  - ICON: icon only
 *  - SHORT: short label only
 */
export const TagChip: FC<{ tag: TagData; size?: "small" | "medium" }> = ({
  tag,
  size = "small",
}) => {
  const { t } = useTranslation()
  const display = tag.displayOption || "ICON_SHORT"
  const label = String(t(tag.shortLabel || tag.label || "", tag.shortLabel || tag.label || ""))
  const showIcon = display === "ICON" || display === "ICON_SHORT"
  const showLabel = display === "SHORT" || display === "ICON_SHORT"
  const iconSize = size === "small" ? 10 : 13
  const fontSize = size === "small" ? 11 : 12
  const height = size === "small" ? 22 : 26
  const px = size === "small" ? 0.75 : 1

  return (
    <Tooltip title={String(t(tag.label || "", tag.label || ""))} enterDelay={400}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          bgcolor: tag.color || "grey.400",
          color: "#fff",
          borderRadius: 1,
          height,
          px,
          fontSize,
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {showIcon && <FaIcon icon={tag.icon || "tag"} color="#fff" size={iconSize} />}
        {showLabel && label && <span>{label}</span>}
      </Box>
    </Tooltip>
  )
}
